import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Play, Pause } from "lucide-react";

/**
 * Cinematic full-bleed showreel.
 * Autoplays muted on loop; user can unmute and play/pause.
 * Variants:
 *   - "hero"    : full-viewport immersive hero with overlay text
 *   - "section" : 16:9 widescreen section (dedicated showreel area)
 */
const Showreel = ({
  src = "/showreel.mp4",
  poster = "/showreel-poster.jpg",
  variant = "section",
  eyebrow = "SHOWREEL",
  title,
  subtitle,
  children,
}) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    // Attempt autoplay (muted) on mount
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, []);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  const isHero = variant === "hero";

  return (
    <section
      className={
        isHero
          ? "relative w-full h-screen min-h-[640px] overflow-hidden bg-black"
          : "relative w-full overflow-hidden bg-black"
      }
      data-testid="studio-showreel"
    >
      <div
        className={
          isHero
            ? "absolute inset-0"
            : "relative w-full aspect-video"
        }
      >
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
          data-testid="showreel-video"
        />

        {/* Cinematic gradient overlay */}
        <div
          className={
            isHero
              ? "absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black"
              : "absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/60 pointer-events-none"
          }
        />

        {/* Film grain texture */}
        <div className="absolute inset-0 film-grain opacity-40 pointer-events-none" />

        {/* Controls */}
        <div className="absolute bottom-6 right-6 flex items-center gap-3 z-20">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? "Pause showreel" : "Play showreel"}
            className="w-11 h-11 flex items-center justify-center rounded-full border border-white/40 bg-black/40 backdrop-blur-md text-white hover:bg-[#F97316] hover:border-[#F97316] transition-colors"
            data-testid="showreel-play-toggle"
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? "Unmute showreel" : "Mute showreel"}
            className="px-4 h-11 flex items-center gap-2 rounded-full border border-white/40 bg-black/40 backdrop-blur-md text-white hover:bg-[#F97316] hover:border-[#F97316] transition-colors"
            data-testid="showreel-mute-toggle"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            <span className="text-xs tracking-widest uppercase">
              {muted ? "Unmute" : "Mute"}
            </span>
          </button>
        </div>

        {/* Overlay content for hero variant */}
        {isHero && (title || children) && (
          <div className="absolute inset-0 flex items-end md:items-center z-10 px-6 md:px-16 pb-24 md:pb-0">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-4xl"
            >
              {eyebrow && (
                <div className="text-xs md:text-sm tracking-[0.4em] text-[#F97316] mb-4">
                  {eyebrow}
                </div>
              )}
              {title && (
                <h1 className="heading-font text-5xl md:text-7xl lg:text-8xl text-white leading-[0.95]">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="mt-6 text-lg md:text-xl text-gray-200 max-w-2xl">
                  {subtitle}
                </p>
              )}
              {children && <div className="mt-8">{children}</div>}
            </motion.div>
          </div>
        )}
      </div>

      {/* Section-variant caption */}
      {!isHero && title && (
        <div className="absolute top-6 left-6 md:top-10 md:left-10 z-10">
          <div className="text-xs tracking-[0.4em] text-[#F97316] mb-2">
            {eyebrow}
          </div>
          <h2 className="heading-font text-3xl md:text-5xl text-white drop-shadow-lg">
            {title}
          </h2>
        </div>
      )}
    </section>
  );
};

export default Showreel;
